import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Gamepad2, Coins, Play, Clock, Users, Trophy } from "lucide-react";
import TopNavbar from "../../TopNavbar";
import SignupModal from "../../SignupModal";
import LoginModal from "../../LoginModal";
import { useUser } from "../../UserContext";
import { getLeaderboard, getUserScore, submitScore } from "../../lib/tournamentService";
import GameLauncher from "../../Components/GameLauncher";

const TABS = [
  { id: "daily", label: "Diario", title: "Ganadores del día" },
  { id: "weekly", label: "Semanal", title: "Ganadores de la semana" },
  { id: "monthly", label: "Mensual", title: "Ganadores del mes" },
  { id: "allTime", label: "Histórico", title: "Ranking histórico" },
];

const PAGE_SIZE = 10;

const tournamentData = {
  id: "zap-aliens",
  name: "Zap Aliens Championship",
  description: "Compite por el puntaje más alto y llévate el trono galáctico",
  gameUrl: "/zap_alliens/zap-aliens-html5/game/index.html",
  endDate: "2026-07-31",
  active: true,
  totalPlayers: 1000,
};

// Ranking simulado (mismo dato por ahora para todos los tabs)
const mockRanking = [
  { userId: "1", displayName: "Angelcris", score: 30, photoURL: null },
  { userId: "2", displayName: "Guilherme", score: 25, photoURL: null },
  { userId: "3", displayName: "Abdullah", score: 20, photoURL: null },
  { userId: "4", displayName: "CosmicNinja", score: 18, photoURL: null },
  { userId: "5", displayName: "StarBlaster", score: 15, photoURL: null },
  { userId: "6", displayName: "NebulaKing", score: 12, photoURL: null },
  { userId: "7", displayName: "VoidWalker", score: 10, photoURL: null },
  { userId: "8", displayName: "PlasmaGhost", score: 8, photoURL: null },
  { userId: "9", displayName: "MeteorHead", score: 6, photoURL: null },
  { userId: "10", displayName: "OrionRider", score: 5, photoURL: null },
  { userId: "11", displayName: "Sergei", score: 4, photoURL: null },
  { userId: "12", displayName: "Saipriya", score: 3, photoURL: null },
  { userId: "13", displayName: "Ava", score: 2, photoURL: null },
  { userId: "14", displayName: "Jade", score: 1, photoURL: null },
  { userId: "15", displayName: "Mrimmoaee", score: 1, photoURL: null },
  { userId: "16", displayName: "Draco", score: 0, photoURL: null },
  { userId: "17", displayName: "Nova", score: 0, photoURL: null },
  { userId: "18", displayName: "Kyros", score: 0, photoURL: null },
  { userId: "19", displayName: "Zara", score: 0, photoURL: null },
  { userId: "20", displayName: "Titan", score: 0, photoURL: null },
];

const Avatar = ({ name, photoURL, size = 40, ringColor = "border-white/20" }) => {
  const initial = name?.charAt(0)?.toUpperCase() || "?";
  const colors = ["from-pink-500", "from-purple-500", "from-orange-500", "from-blue-500", "from-green-500"];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];

  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt={name}
        style={{ width: size, height: size }}
        className={`rounded-full border-2 ${ringColor}`}
      />
    );
  }

  return (
    <div
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      className={`rounded-full bg-gradient-to-br ${color} to-indigo-600 flex items-center justify-center font-bold border-2 ${ringColor}`}
    >
      {initial}
    </div>
  );
};

const TournamentScreen = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [activeTab, setActiveTab] = useState("daily");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [ranking, setRanking] = useState([]);
  const [userScore, setUserScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGameUrl, setActiveGameUrl] = useState(null);

  // Escucha el postMessage del juego para guardar el puntaje
  useEffect(() => {
    const lastScore = { current: null };
    const handleMessage = async (event) => {
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.type !== "GAME_SCORE" || data.gameId !== tournamentData.id) return;
      const score = Number(data.score);
      if (!Number.isFinite(score) || score < 0) return;
      if (lastScore.current === score) return;
      if (!user) return;
      try {
        await submitScore(tournamentData.id, score, user);
        lastScore.current = score;
      } catch (err) {
        console.error("Error guardando puntaje:", err);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      getLeaderboard(tournamentData.id, 100),
      user ? getUserScore(tournamentData.id, user.uid) : Promise.resolve(null),
    ])
      .then(([leaderboard, myScore]) => {
        if (cancelled) return;
        setRanking(leaderboard);
        setUserScore(myScore);
      })
      .catch((err) => console.error("Error cargando torneo:", err))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [user, activeTab]);

  const currentTab = TABS.find((t) => t.id === activeTab);
  const [first, second, third] = ranking;
  const totalPlayers = ranking.length;
  const visiblePlayers = ranking.slice(0, visibleCount);
  const hasMore = visibleCount < ranking.length;

  const currentUserRank = user
    ? ranking.findIndex((p) => p.userId === user.uid) + 1 || null
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex flex-col overflow-x-hidden">
      <TopNavbar
        onSignUpClick={() => setShowSignup(true)}
        onLoginClick={() => setShowLogin(true)}
      />

      <SignupModal
        isOpen={showSignup}
        onClose={() => setShowSignup(false)}
        onSwitchToLogin={() => {
          setShowSignup(false);
          setShowLogin(true);
        }}
      />
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToSignup={() => {
          setShowLogin(false);
          setShowSignup(true);
        }}
      />

      <div className="flex flex-1 overflow-hidden">
        <div className="hidden sm:flex bg-white/5 text-white backdrop-blur-lg shadow-md h-full py-6 px-4 flex-col items-center gap-6 overflow-hidden border-r border-white/10">
          <button onClick={() => navigate("/juegos")} className="p-2 rounded-lg hover:bg-white/10 transition" title="Juegos">
            <Gamepad2 size={24} />
          </button>
          <button onClick={() => navigate("/torneos/zap-aliens")} className="p-2 rounded-lg hover:bg-white/10 transition text-yellow-400" title="Torneos">
            <Crown size={24} />
          </button>
        </div>

        <div className="fixed bottom-0 left-0 right-0 w-full bg-white/5 backdrop-blur-lg shadow-inner border-t border-white/10 sm:hidden z-50">
          <div className="max-w-md mx-auto flex justify-around items-center py-3 text-white text-sm">
            <button onClick={() => navigate("/juegos")} className="flex flex-col items-center">
              <Gamepad2 size={20} />
              <span className="text-xs mt-1">Juegos</span>
            </button>
            <button onClick={() => navigate("/torneos/zap-aliens")} className="flex flex-col items-center text-yellow-400">
              <Crown size={20} />
              <span className="text-xs mt-1">Torneos</span>
            </button>
          </div>
        </div>

        <div className="flex-1 px-6 py-8 overflow-y-auto pb-20">
          <div className="max-w-3xl mx-auto space-y-6">

            {/* Hero del torneo */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl overflow-hidden bg-white/5 border border-white/10 backdrop-blur-md p-6"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg">
                    <Trophy size={48} className="text-white" />
                  </div>
                </div>

                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <span className="px-3 py-1 rounded-full bg-green-500/20 border border-green-500/40 text-green-300 text-xs font-semibold uppercase tracking-wider">
                      {tournamentData.active ? "En vivo" : "Finalizado"}
                    </span>
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <Clock size={14} />
                      {(() => {
                        const days = Math.ceil((new Date(tournamentData.endDate) - new Date()) / (1000 * 60 * 60 * 24));
                        return days > 0 ? `${days} días restantes` : "Finalizado";
                      })()}
                    </span>
                  </div>

                  <h1 className="text-3xl md:text-4xl font-bold mb-2">{tournamentData.name}</h1>
                  <p className="text-gray-300 mb-3">{tournamentData.description}</p>

                  <div className="flex items-center gap-4 justify-center md:justify-start text-sm text-gray-400">
                    <span className="flex items-center gap-1">
                      <Users size={16} />
                      {tournamentData.totalPlayers} jugadores
                    </span>
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveGameUrl(tournamentData.gameUrl)}
                  className="flex items-center gap-2 bg-white text-black px-6 py-3 rounded-xl font-bold shadow-lg"
                >
                  <Play size={20} fill="black" />
                  Jugar ahora
                </motion.button>
              </div>
            </motion.div>

            {/* Tabs de periodo */}
            <div className="bg-white/5 border border-white/10 rounded-full p-1 backdrop-blur-md flex">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold transition ${
                    activeTab === tab.id
                      ? "bg-white text-gray-900 shadow-lg"
                      : "text-gray-300 hover:text-white"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Título del período */}
            <h1 className="text-2xl font-bold">{currentTab.title}</h1>

            {/* Estado vacío */}
            {!loading && ranking.length === 0 && (
              <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md p-8 text-center">
                <Trophy size={48} className="text-yellow-400 mx-auto mb-3 opacity-60" />
                <p className="text-lg font-semibold mb-1">Aún no hay jugadores en el ranking</p>
                <p className="text-sm text-gray-400">¡Sé el primero en marcar puntaje!</p>
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="text-center py-8 text-gray-400">Cargando ranking...</div>
            )}

            {/* Podio Top 3 */}
            {!loading && ranking.length > 0 && (
            <div className="grid grid-cols-3 gap-3 items-end pt-8">
              {/* 2do lugar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative rounded-2xl bg-gradient-to-b from-gray-400 to-gray-600 p-4 pt-6 shadow-xl h-44 flex flex-col items-center justify-end"
              >
                <span className="absolute top-2 right-3 bg-black/20 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  2
                </span>
                <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                  <Avatar name={second?.displayName} photoURL={second?.photoURL} size={64} ringColor="border-white" />
                </div>
                <p className="font-bold text-center truncate w-full">{second?.displayName}</p>
                <div className="flex items-center gap-1 mt-1 bg-black/20 px-2 py-0.5 rounded">
                  <Coins size={12} className="text-yellow-300" />
                  <span className="text-sm font-bold">{second?.score}</span>
                </div>
              </motion.div>

              {/* 1er lugar */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative rounded-2xl bg-gradient-to-b from-yellow-400 to-yellow-600 p-4 pt-6 shadow-2xl h-56 flex flex-col items-center justify-end"
              >
                <Crown className="absolute -top-8 left-1/2 -translate-x-1/2 text-yellow-300" size={40} fill="currentColor" />
                <span className="absolute top-2 right-3 bg-black/20 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  1
                </span>
                <div className="absolute top-4 left-1/2 -translate-x-1/2">
                  <Avatar name={first?.displayName} photoURL={first?.photoURL} size={80} ringColor="border-white" />
                </div>
                <p className="font-bold text-center truncate w-full text-lg">{first?.displayName}</p>
                <div className="flex items-center gap-1 mt-1 bg-black/20 px-3 py-1 rounded">
                  <Coins size={14} className="text-yellow-300" />
                  <span className="text-base font-bold">{first?.score}</span>
                </div>
              </motion.div>

              {/* 3er lugar */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative rounded-2xl bg-gradient-to-b from-orange-500 to-orange-700 p-4 pt-6 shadow-xl h-40 flex flex-col items-center justify-end"
              >
                <span className="absolute top-2 right-3 bg-black/20 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  3
                </span>
                <div className="absolute -top-8 left-1/2 -translate-x-1/2">
                  <Avatar name={third?.displayName} photoURL={third?.photoURL} size={64} ringColor="border-white" />
                </div>
                <p className="font-bold text-center truncate w-full">{third?.displayName}</p>
                <div className="flex items-center gap-1 mt-1 bg-black/20 px-2 py-0.5 rounded">
                  <Coins size={12} className="text-yellow-300" />
                  <span className="text-sm font-bold">{third?.score}</span>
                </div>
              </motion.div>
            </div>
            )}

            {/* Global / Total players */}
            <div className="flex justify-between items-center pt-4 text-sm">
              <span className="text-cyan-400 font-semibold">Global</span>
              <span className="text-gray-400">{totalPlayers} jugadores</span>
            </div>

            {/* Card del usuario actual */}
            <div className="rounded-2xl bg-gradient-to-r from-purple-600 to-purple-800 p-4 flex items-center gap-4 shadow-xl">
              <div className="text-center">
                <p className="text-xs text-purple-200 uppercase tracking-wider">Puesto</p>
                <p className="text-2xl font-black">{currentUserRank || "N/A"}</p>
              </div>
              <Avatar
                name={user?.displayName || "Invitado"}
                photoURL={user?.photoURL}
                size={48}
                ringColor="border-white/30"
              />
              <div className="flex-1">
                <p className="font-bold">{user?.displayName || "Tú"}</p>
                <div className="flex items-center gap-1 text-sm">
                  <Coins size={12} className="text-yellow-300" />
                  <span>{userScore?.score || 0}</span>
                </div>
              </div>
            </div>

            {/* Tabla de ranking */}
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-6 py-3 border-b border-white/10 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                <div className="col-span-1">#</div>
                <div className="col-span-8">Jugador</div>
                <div className="col-span-3 text-right">Puntaje</div>
              </div>

              <div className="divide-y divide-white/5">
                {visiblePlayers.map((player, index) => {
                  const position = index + 1;
                  const isCurrentUser = user?.uid === player.userId;

                  return (
                    <motion.div
                      key={player.userId}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className={`grid grid-cols-12 gap-2 px-6 py-3 items-center transition ${
                        isCurrentUser ? "bg-purple-500/10" : "hover:bg-white/5"
                      }`}
                    >
                      <div className="col-span-1 font-semibold text-gray-400">{position}</div>
                      <div className="col-span-8 flex items-center gap-3">
                        <Avatar name={player.displayName} photoURL={player.photoURL} size={36} />
                        <span className="font-semibold truncate">{player.displayName}</span>
                      </div>
                      <div className="col-span-3 flex items-center justify-end gap-1 font-bold">
                        <Coins size={14} className="text-yellow-400" />
                        <span>{player.score}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {hasMore && (
                <button
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 font-bold text-white transition"
                >
                  Ver más
                </button>
              )}
            </div>

          </div>
        </div>
      </div>

      <GameLauncher
        gameUrl={activeGameUrl}
        onClose={() => setActiveGameUrl(null)}
      />
    </div>
  );
};

export default TournamentScreen;
